import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { config } from '../../config';

export default function GererCours() {
  const [cours, setCours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ 
    titre: '', 
    categorie: 'college_lycee', 
    prix: 0, 
    description: '' 
  });
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const API_BASE = config.apiBase;
  const getToken = () => localStorage.getItem('token');

  const fetchCours = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/enseignant/cours/get_mes_cours.php`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setCours(data.cours);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleMediaChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Vérifier la taille (50 Mo max)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 50 Mo)');
      return;
    }

    // Vérifier le type
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf',
      'audio/mpeg', 'audio/ogg', 'audio/wav',
      'video/mp4', 'video/avi', 'video/mpeg', 'video/quicktime'
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error('Type de fichier non autorisé');
      return;
    }

    setSelectedMedia(file);

    // Prévisualisation
    const reader = new FileReader();
    reader.onloadend = () => {
      setMediaPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Déterminer le type
    if (file.type.startsWith('image/')) {
      setMediaType('image');
    } else if (file.type === 'application/pdf') {
      setMediaType('pdf');
    } else if (file.type.startsWith('audio/')) {
      setMediaType('audio');
    } else if (file.type.startsWith('video/')) {
      setMediaType('video');
    }
  };

  const removeMedia = () => {
    setSelectedMedia(null);
    setMediaPreview(null);
    setMediaType('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.titre.trim()) { 
      toast.error('Titre requis'); 
      return; 
    }
    if (formData.prix < 0) { 
      toast.error('Prix invalide'); 
      return; 
    }
    
    setSubmitting(true);
    try {
      const token = getToken();
      const formDataToSend = new FormData();
      formDataToSend.append('titre', formData.titre);
      formDataToSend.append('categorie', formData.categorie);
      formDataToSend.append('prix', formData.prix);
      formDataToSend.append('description', formData.description);
      
      if (selectedMedia) {
        formDataToSend.append('media', selectedMedia);
      }

      const response = await fetch(`${API_BASE}/enseignant/cours/creer_cours.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Cours créé avec succès !');
        setFormData({ titre: '', categorie: 'college_lycee', prix: 0, description: '' });
        removeMedia();
        setShowForm(false);
        fetchCours();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => { fetchCours(); }, []);

  const categories = [
    { value: 'primaire', label: '🏫 École Primaire' },
    { value: 'college_lycee', label: '📚 Collège & Lycée' },
    { value: 'universite', label: '🎓 Université' },
    { value: 'libre', label: '📖 Libre' }
  ];

  // Fonction pour afficher la prévisualisation du média
  const renderMediaPreview = () => {
    if (!mediaPreview) return null;

    switch (mediaType) {
      case 'image':
        return <img src={mediaPreview} alt="Aperçu" className="max-h-48 object-contain rounded" />;
      case 'pdf':
        return (
          <div className="flex items-center gap-3 bg-red-50 p-4 rounded">
            <span className="text-4xl">📄</span>
            <div>
              <p className="font-medium">Document PDF</p>
              <p className="text-sm text-gray-500">{selectedMedia?.name}</p>
            </div>
          </div>
        );
      case 'audio':
        return (
          <div className="flex items-center gap-3 bg-green-50 p-4 rounded w-full">
            <span className="text-3xl">🎵</span>
            <audio controls className="flex-1">
              <source src={mediaPreview} type={selectedMedia?.type} />
            </audio>
          </div>
        );
      case 'video':
        return (
          <div className="relative bg-black rounded overflow-hidden">
            <video controls className="max-h-48 w-full">
              <source src={mediaPreview} type={selectedMedia?.type} />
            </video>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">📚 Mes cours</h1>
          <p className="text-sm text-gray-500">Gérez vos cours et leurs contenus</p>
        </div>
        <button 
          onClick={() => setShowForm(!showForm)} 
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-2 whitespace-nowrap"
        >
          <span className="text-lg">+</span> Nouveau cours
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 mb-6 border">
          <h2 className="text-lg font-bold mb-4">➕ Créer un nouveau cours</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Titre *</label>
              <input 
                type="text" 
                value={formData.titre} 
                onChange={(e) => setFormData({ ...formData, titre: e.target.value })} 
                placeholder="Ex: Mathématiques - Première" 
                className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" 
                required 
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Catégorie *</label>
              <select 
                value={formData.categorie} 
                onChange={(e) => setFormData({ ...formData, categorie: e.target.value })} 
                className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Prix (FCFA)</label>
              <input 
                type="number" 
                value={formData.prix} 
                onChange={(e) => setFormData({ ...formData, prix: parseInt(e.target.value) || 0 })} 
                placeholder="0 = Gratuit" 
                className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" 
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea 
                value={formData.description} 
                onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
                rows="3" 
                placeholder="Décrivez brièvement ce cours..." 
                className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" 
              />
            </div>

            {/* Section Media */}
            <div className="border-t pt-4">
              <label className="block text-sm font-medium mb-2">📎 Média du cours (optionnel)</label>
              
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition border-2 border-dashed border-gray-300 hover:border-blue-400"
                >
                  📤 Choisir un fichier
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf,audio/*,video/*"
                  onChange={handleMediaChange}
                  className="hidden"
                />
                {selectedMedia && (
                  <button
                    type="button"
                    onClick={removeMedia}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    ✕ Supprimer
                  </button>
                )}
              </div>

              {selectedMedia && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">
                    📁 {selectedMedia.name} ({(selectedMedia.size / 1024 / 1024).toFixed(2)} Mo)
                  </p>
                  <div className="flex justify-center">
                    {renderMediaPreview()}
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1">
                Formats acceptés : JPG, PNG, GIF, PDF, MP3, MP4 (max 50 Mo)
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button 
                type="submit" 
                disabled={submitting} 
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Création...' : 'Créer le cours'}
              </button>
              <button 
                type="button" 
                onClick={() => {
                  setShowForm(false);
                  removeMedia();
                }} 
                className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {cours.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <div className="text-5xl mb-4">📚</div>
          <p className="text-gray-500">Aucun cours</p>
          <button onClick={() => setShowForm(true)} className="mt-3 text-blue-600 hover:underline">
            Créez votre premier cours
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cours.map(c => (
            <div key={c.id_cours} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition border">
              <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-lg text-gray-800 truncate">{c.titre}</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {categories.find(cat => cat.value === c.categorie)?.label || c.categorie}
                    </p>
                  </div>
                  <span className="text-lg font-bold text-green-600 whitespace-nowrap ml-2">
                    {c.prix.toLocaleString()} F
                  </span>
                </div>
                {/* Affichage du média dans la carte */}
                {c.media_url && (
                  <div className="mt-2">
                    {c.media_type === 'image' && (
                      <img 
                        src={config.normalizePostMedia(c.media_url)} 
                        alt={c.titre}
                        className="w-full h-32 object-cover rounded"
                      />
                    )}
                    {c.media_type === 'pdf' && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 bg-white/50 p-2 rounded">
                        <span>📄</span>
                        <span>PDF</span>
                      </div>
                    )}
                    {c.media_type === 'audio' && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 bg-white/50 p-2 rounded">
                        <span>🎵</span>
                        <span>Audio</span>
                      </div>
                    )}
                    {c.media_type === 'video' && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 bg-white/50 p-2 rounded">
                        <span>🎬</span>
                        <span>Vidéo</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                  {c.description || 'Aucune description'}
                </p>
                <div className="flex justify-between items-center text-sm text-gray-500 mb-3">
                  <span>📄 {c.nb_pages || 0} pages</span>
                  <span>📅 {new Date(c.date_creation).toLocaleDateString()}</span>
                </div>
                <button 
                  onClick={() => navigate(`/enseignant/cours/${c.id_cours}/pages`)} 
                  className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-2"
                >
                  ✏️ Gérer les pages
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}